import { knownErrorCodeMessage } from './error-codes'

export const EMAIL_ALREADY_REGISTERED = 'EMAIL_ALREADY_REGISTERED'

export type ParsedApiError = {
  message?: string
  errorCode?: string
  fieldErrors: Record<string, string>
  hasResponse: boolean
}

const GENERIC_MESSAGES = new Set([
  'es ist ein fehler aufgetreten.',
  'an error occurred.',
  'internal server error',
  'bad request',
  'unauthorized',
  'forbidden',
  'not found',
  'conflict',
  'http exception',
  'unprocessable entity',
])

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

/** Human-readable API text. Drops blank strings and status labels that are not useful in the UI. */
export function usefulApiMessage(value: unknown): string | undefined {
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed || GENERIC_MESSAGES.has(trimmed.toLowerCase())) return undefined
    return trimmed
  }

  if (Array.isArray(value)) {
    const parts = value
      .map(item => usefulApiMessage(item))
      .filter((item): item is string => Boolean(item))
    return parts.length > 0 ? parts.join(' ') : undefined
  }

  return undefined
}

function readFieldErrors(value: unknown): Record<string, string> {
  if (!Array.isArray(value)) return {}

  const fields: Record<string, string> = {}
  for (const item of value) {
    if (!isRecord(item)) continue
    const field = typeof item.field === 'string' ? item.field.trim() : ''
    const message = usefulApiMessage(item.message)
    if (!field || !message || fields[field]) continue
    fields[field] = message
  }
  return fields
}

/**
 * Read a Nest/axios error body.
 * `message` is the banner text. `errors[].field` + `errors[].message` mark inputs.
 */
export function parseApiError(error: unknown): ParsedApiError {
  const response = isRecord(error) ? error.response : undefined
  const hasResponse = isRecord(response)
  const data = hasResponse ? response.data : undefined

  if (!isRecord(data)) {
    return {
      message: typeof data === 'string' ? usefulApiMessage(data) : undefined,
      fieldErrors: {},
      hasResponse,
    }
  }

  const errorCode =
    typeof data.errorCode === 'string' && data.errorCode.trim() ? data.errorCode.trim() : undefined

  return {
    message: usefulApiMessage(data.message),
    errorCode,
    fieldErrors: readFieldErrors(data.errors),
    hasResponse,
  }
}

export function isEmailAlreadyRegistered(parsed: ParsedApiError): boolean {
  return parsed.errorCode === EMAIL_ALREADY_REGISTERED
}

export function apiErrorBanner(
  parsed: ParsedApiError,
  fallback: { network: string; unknown: string }
): string {
  if (parsed.message) return parsed.message

  const fieldText = Object.values(parsed.fieldErrors).filter(Boolean).join(' ')
  if (fieldText) return fieldText

  const known = knownErrorCodeMessage(parsed.errorCode)
  if (known) return known

  return parsed.hasResponse ? fallback.unknown : fallback.network
}
